import { DemoFlow, usePageTracking } from '@adthlete/shared-frontend'
import { Toaster as Sonner } from '@/components/ui/sonner'
import { TooltipProvider } from '@/components/ui/tooltip'

import {
  createBrowserRouter,
  RouterProvider,
  ScrollRestoration,
  Outlet,
  Navigate,
} from 'react-router-dom'

import Layout from './components/Layout'
import Dashboard from './pages/Dashboard'
import Collaborations from './pages/Collaborations'
import Help from './pages/Help'
import CollaborationDetail from './pages/CollaborationDetail'
import SubmitCreativeConcept from './pages/SubmitCreativeConcept'
import UploadVideoReview from './pages/UploadVideoReview'
import UploadFinalVideo from './pages/UploadFinalVideo'
import CollaborationLive from './pages/CollaborationLive'
import OfferDetail from './pages/OfferDetail'
import Performance from './pages/Performance'
import Payments from './pages/Payments'
import InvoiceReview from './pages/InvoiceReview'
import StripeReturn from './pages/StripeReturn'
import StripeRefresh from './pages/StripeRefresh'
import PendingContent from './pages/PendingContent'
import ApprovedContent from './pages/ApprovedContent'
import FinalVideo from './pages/FinalVideo'
import ScheduledPost from './pages/ScheduledPost'
import Settings from './pages/Settings'
import PublicProfilePage from './pages/PublicProfilePage'
import InstagramConnect from './pages/InstagramConnect'
import InstagramCallback from './pages/InstagramCallback'
import YouTubeConnect from './pages/YouTubeConnect'
import YouTubeCallback from './pages/YouTubeCallback'
import YouTubeAuthSteps from './pages/YouTubeAuthSteps'
import TikTokConnect from './pages/TikTokConnect'
import TikTokCallback from './pages/TikTokCallback'
import TikTokAuthSteps from './pages/TikTokAuthSteps'
import NotFound from './pages/NotFound'
import SignIn from './pages/SignIn'
import Onboarding from './pages/Onboarding'
import VerifyEmail from './pages/VerifyEmail'
import EmailConfirmation from './pages/EmailConfirmation'
import ForgotPassword from './pages/ForgotPassword'
import SignInWithEmail from './pages/SignInWithEmail'
import VerifyMagicLink from './pages/VerifyMagicLink'
import ResetPassword from './pages/ResetPassword'
import TermsAndConditions from './pages/TermsAndConditions'
import VideoInfo from './pages/VideoInfo'
import PublicProfileView from './pages/PublicProfileView'
import LicensingPage from './pages/LicensingPage'
import AcceptManagerInvite from './pages/AcceptManagerInvite'
import AcceptMarketerInvite from './pages/AcceptMarketerInvite'
import ClaimAccount from './pages/ClaimAccount'
import Messages from './pages/Messages'
import CreatePassword from './pages/setup/CreatePassword'
import AcceptTerms from './pages/setup/AcceptTerms'
import Welcome from './pages/Welcome'
import AboutYou from './pages/setup/AboutYou'
import PublishProfile from './pages/setup/PublishProfile'
import GetTheApp from './pages/setup/GetTheApp'
import RateCardPage from './pages/setup/RateCard'
import ContentThemes from './pages/setup/ContentThemes'
import Opportunities from './pages/Opportunities'
import AcademyLesson from './pages/AcademyLesson'
import Academy from './pages/Academy'
import Deals from './pages/Deals'
import DealScreen from './pages/DealScreen'
import DealRedirect from './pages/DealRedirect'
import OpportunityApply from './pages/OpportunityApply'
import BrandView from './pages/BrandView'
import ConnectAccounts from './pages/setup/ConnectAccounts'
import PayoutAndAddress from './pages/setup/PayoutAndAddress'
import ComingSoonPage from './pages/ComingSoonPage'
import CalendarView from './pages/CalendarView'
import CalendarSetup from './pages/CalendarSetup'
import GoogleCalendarConnect from './pages/GoogleCalendarConnect'
import GoogleCalendarCallback from './pages/GoogleCalendarCallback'
import OutlookCalendarConnect from './pages/OutlookCalendarConnect'
import OutlookCalendarCallback from './pages/OutlookCalendarCallback'
import ProtectedRoute from './components/ProtectedRoute'
import ErrorBoundary, { ErrorFallback } from './components/ErrorBoundary'
import { store } from './store'
import { Provider } from 'react-redux'
import { CampaignsProvider } from './contexts/CampaignsContext'

function RootLayout() {
  // Page views, time on each screen, exits and abandoned forms, for the admin Behavior page
  usePageTracking('athlete')
  return (
    <CampaignsProvider>
      <ScrollRestoration />
      <Outlet />
    </CampaignsProvider>
  )
}

const basename = (import.meta.env.VITE_BASE ?? '/').replace(/\/$/, '')

const router = createBrowserRouter(
  [
    {
      element: <RootLayout />,
      errorElement: <ErrorFallback />,
      children: [
        { path: '/signin', element: <SignIn /> },
        { path: '/signin-email', element: <SignInWithEmail /> },
        { path: '/verify-magic-link/:token', element: <VerifyMagicLink /> },
        { path: '/onboarding', element: <Onboarding /> },
        { path: '/verify-email', element: <VerifyEmail /> },
        { path: '/confirm-email', element: <EmailConfirmation /> },
        { path: '/forgot-password', element: <ForgotPassword /> },
        { path: '/reset-password', element: <ResetPassword /> },
        { path: '/terms', element: <TermsAndConditions /> },
        { path: '/how-to-earn', element: <VideoInfo /> },
        { path: '/p/:username', element: <PublicProfileView /> },
        { path: '/manager-invite/:token', element: <AcceptManagerInvite /> },
        { path: '/marketer-invite/:token', element: <AcceptMarketerInvite /> },
        { path: '/claim-account/:token', element: <ClaimAccount /> },
        {
          element: <ProtectedRoute />,
          children: [
            // Create-password screen is full-bleed (no app chrome), like the phone gate
            { path: '/start', element: <CreatePassword /> },
            { path: '/setup/terms', element: <AcceptTerms /> },
            {
              element: <Layout />,
              children: [
                { path: '/', element: <Dashboard /> },
                // The old quest screens: links in emails and notifications still point here
                { path: 'setup/rank', element: <Navigate to="/welcome" replace /> },
                { path: 'setup/quests', element: <Navigate to="/brand-view" replace /> },
                { path: 'setup/about', element: <AboutYou /> },
                { path: 'setup/publish', element: <PublishProfile /> },
                { path: 'setup/app', element: <GetTheApp /> },
                { path: 'setup/connect', element: <ConnectAccounts /> },
                { path: 'setup/payout', element: <PayoutAndAddress /> },
                { path: 'setup/rates', element: <RateCardPage /> },
                { path: 'setup/themes', element: <ContentThemes /> },
                { path: 'setup/home', element: <Navigate to="/" replace /> },
                // Deals: one list and one screen per deal (replaces Collaborations/OfferDetail)
                { path: 'deals', element: <Deals /> },
                { path: 'deals/:id', element: <DealScreen /> },
                { path: 'collaborations', element: <Navigate to="/deals" replace /> },
                // The Marketplace: opportunities by type, and one screen to apply
                { path: 'opportunities', element: <Navigate to="/opportunities/all" replace /> },
                { path: 'opportunities/:type', element: <Opportunities /> },
                { path: 'opportunity/:id', element: <OpportunityApply /> },
                // Creator Academy: how-to lessons (?v=<lesson> plays one)
                { path: 'academy', element: <Academy /> },
                { path: 'academy/:lesson', element: <AcademyLesson /> },
                { path: 'campaigns', element: <Navigate to="/deals" replace /> },
                { path: 'messages', element: <Messages /> },
                { path: 'messages/:id', element: <Messages /> },
                { path: 'bids/:id', element: <DealRedirect /> },
                { path: 'collaborations/:id', element: <DealRedirect /> },
                { path: 'campaigns/:id', element: <DealRedirect /> },
                // Invoices review on their own page; ?invoice= names a license-extension invoice
                { path: 'collaborations/:id/invoice', element: <InvoiceReview /> },
                { path: 'campaigns/:id/invoice', element: <InvoiceReview /> },
                // The other old step pages (concept, upload, live, licensing) are stages of
                // the deal screen now; their links land there
                { path: 'collaborations/:id/*', element: <DealRedirect /> },
                { path: 'campaigns/:id/*', element: <DealRedirect /> },
                { path: 'brand-view', element: <BrandView /> },
                { path: 'scheduling/:id', element: <ScheduledPost /> },
                { path: 'performance', element: <Performance /> },
                { path: 'public-profile', element: <PublicProfilePage /> },
                { path: 'earnings', element: <Payments /> },
                { path: 'earnings/stripe-return', element: <StripeReturn /> },
                { path: 'earnings/stripe-refresh', element: <StripeRefresh /> },
                { path: 'pending', element: <PendingContent /> },
                { path: 'approved', element: <ApprovedContent /> },
                { path: 'settings', element: <Settings /> },
                // Shown once after sign-up: any offer waiting, and the first month with the Lab
                { path: 'welcome', element: <Welcome /> },
                { path: 'help', element: <Help /> },
                { path: 'tools/caption-writer', element: <ComingSoonPage /> },
                { path: 'tools/content-ideas', element: <ComingSoonPage /> },
                { path: 'tools/best-time', element: <ComingSoonPage /> },
                { path: 'tools/schedule-post', element: <ComingSoonPage /> },
                { path: 'connect/instagram', element: <InstagramConnect /> },
                { path: 'instagram/callback', element: <InstagramCallback /> },
                { path: 'connect/youtube', element: <YouTubeConnect /> },
                { path: 'youtube/callback', element: <YouTubeCallback /> },
                { path: 'youtube/auth-steps', element: <YouTubeAuthSteps /> },
                { path: 'connect/tiktok', element: <TikTokConnect /> },
                { path: 'tiktok/callback', element: <TikTokCallback /> },
                { path: 'tiktok/auth-steps', element: <TikTokAuthSteps /> },
                { path: 'calendar', element: <CalendarView /> },
                { path: 'calendar-setup', element: <CalendarSetup /> },
                { path: 'connect/google-calendar', element: <GoogleCalendarConnect /> },
                { path: 'gcal/callback', element: <GoogleCalendarCallback /> },
                { path: 'connect/outlook-calendar', element: <OutlookCalendarConnect /> },
                { path: 'outlook/callback', element: <OutlookCalendarCallback /> },
                { path: '*', element: <NotFound /> },
              ],
            },
          ],
        },
      ],
    },
  ],
  { basename },
)

const App = () => (
  <Provider store={store}>
    <TooltipProvider>
      <Sonner />
      <RouterProvider router={router} />
      {/* Presenter's guide for demos (?demo=1); never on production */}
      <DemoFlow app="athlete" />
    </TooltipProvider>
  </Provider>
)

export default App
